import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { columnFilterSchema } from "../../common/columnFilterSchema";
import { dataReportStatsSchema } from "zodios/common/statsSchema";

export const targetReportApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getV4TargetReportByTarget",
    method: "get",
    path: "/v4/query/",
    parameters: [
      {
        name: "search",
        type: "Query",
        schema: z.string().array().optional(),
      },
      {
        name: "marketplace",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "table",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "grouping",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "start_date",
        type: "Query",
        schema: z.string().datetime(),
      },
      {
        name: "end_date",
        type: "Query",
        schema: z.string().datetime(),
      },
      {
        name: "start_date_previous",
        type: "Query",
        schema: z.string().datetime(),
      },
      {
        name: "end_date_previous",
        type: "Query",
        schema: z.string().datetime(),
      },
      {
        name: "filters",
        type: "Query",
        schema: z
          .array(
            z.object({
              id: z.string(),
              value: z.object({
                op: z.string().transform((op) => {
                  return op;
                }),
                value: z.unknown(),
              }),
            }),
          )
          .optional(),
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    status: 200,
    response: z.object({
      data: z.array(
        z
          .object({
            adv_keyword_id: z.number().optional(),
            adv_target_id: z.number().optional(),
            creation_date: z.string().date(),
            // Below is to parse targeting expressions for advertised targets
            text: z.string().transform((targetingExpression) => {
              try {
                return JSON.parse(targetingExpression);
              } catch {
                return targetingExpression;
              }
            }),
            match_type: z.string(),
            bid: z.number(),
            manual_bid: z.number().nullable(),
            floor_bid: z.number(),
            ceil_bid: z.number(),
            state: z.string(),
            ad_id: z.number(),
            ad_group_id: z.string().optional(),
            ad_group_name: z.string().optional(),
            source: z.string(),
            tags: z
              .string()
              .transform((tags) => {
                try {
                  return JSON.parse(tags) as string[];
                } catch {
                  return [];
                }
              })
              .nullable(),
            scheduled_actions: z.array(
              z.object({
                scheduled_action_id: z.number(),
                scheduled_action_start_date: z.string().optional(),
                scheduled_action_end_date: z.string().optional(),
                scheduled_action_fields: z.string().transform((spec) => {
                  try {
                    return JSON.parse(spec) as {
                      change_type?: string;
                      change_value?: number;
                    }[];
                  } catch {
                    return [];
                  }
                }),
                scheduled_action_status: z.string().optional(),
              }),
            ),
            refinements: z
              .string()
              .transform((refinements) => {
                try {
                  return JSON.parse(refinements) as string[];
                } catch {
                  return {};
                }
              })
              .nullable()
              .optional(),
          })
          .merge(dataReportStatsSchema),
      ),
      cursor: z
        .object({
          hasPreviousPage: z.boolean(),
          hasNextPage: z.boolean(),
          startCursor: z.string().nullable(),
          endCursor: z.string().nullable(),
        })
        .nullable(),
      count: z.number(),
    }),
  })
    .addEndpoint({
      alias: "getV4TargetReportByDate",
      method: "get",
      path: "/v4/query/?grouping=start_date&limit=1000000",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "table",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "date_group",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "filters",
          type: "Query",
          schema: z
            .array(
              z.object({
                id: z.string(),
                value: z.object({
                  op: z.string().transform((op) => {
                    return op;
                  }),
                  value: z.unknown(),
                }),
              }),
            )
            .optional(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(
          z
            .object({
              start_date: z.string().date(),
            })
            .merge(dataReportStatsSchema),
        ),
        cursor: z
          .object({
            hasPreviousPage: z.boolean(),
            hasNextPage: z.boolean(),
            startCursor: z.string().nullable(),
            endCursor: z.string().nullable(),
          })
          .nullable(),
        count: z.number(),
      }),
    })
    .addEndpoint({
      alias: "getV4TargetReportTotals",
      method: "get",
      path: "/v4/query/aggregate/",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "table",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "filters",
          type: "Query",
          schema: z
            .array(
              z.object({
                id: z.string(),
                value: z.object({
                  op: z.string().transform((op) => {
                    return op;
                  }),
                  value: z.unknown(),
                }),
              }),
            )
            .optional(),
        },
      ],
      status: 200,
      response: z.object({
        data: dataReportStatsSchema,
      }),
    })
    .addEndpoint({
      alias: "getV4TargetReportExport",
      method: "get",
      path: "/v4/query/?xls=true",
      parameters: [
        {
          name: "table",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "report_type",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.instanceof(Blob),
    })
    .build(),
);
